import React, { useState, useEffect } from 'react';
import { useDashboard } from './DashboardContext';
import { streamApi } from '../../services/api';
import {
  Radio,
  X,
  ArrowUpRight,
  RefreshCw,
  Check,
  Calendar,
  AlertCircle
} from 'lucide-react';

const StreamManagerPage = () => {
  const {
    streamData,
    setStreamData,
    handleGoLive
  } = useDashboard();

  const [savingZoom, setSavingZoom] = useState(false);
  const [zoomSaved, setZoomSaved] = useState(false);
  const [schedule, setSchedule] = useState([]);
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [cancelReason, setCancelReason] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Fetch weekly schedule
  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const data = await streamApi.getSchedule();
        setSchedule(data.schedule || []);
      } catch (error) {
        console.error('Failed to fetch schedule:', error);
      } finally {
        setLoadingSchedule(false);
      }
    };
    fetchSchedule();
  }, []);

  const onSaveZoomLink = async () => {
    setSavingZoom(true);
    setZoomSaved(false);
    try {
      const result = await streamApi.update({
        zoom_link: streamData.zoomLink,
        session_description: streamData.sessionDescription || ''
      });
      console.log('Zoom link saved successfully:', result);
      setZoomSaved(true);
      setTimeout(() => setZoomSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save zoom link:', error);
      // Check if it's a network error vs API error
      if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        alert('Network error. Please check your connection and try again.');
      } else {
        alert('Failed to save. Please try again.');
      }
    } finally {
      setSavingZoom(false);
    }
  };

  const handlePlanCancellation = async () => {
    if (!selectedDate) return;
    try {
      await streamApi.planCancellation(selectedDate, cancelReason);
      // Refresh schedule
      const data = await streamApi.getSchedule();
      setSchedule(data.schedule || []);
      setShowCancelModal(false);
      setCancelReason('');
      setSelectedDate(null);
    } catch (error) {
      console.error('Failed to plan cancellation:', error);
      alert('Failed to plan cancellation');
    }
  };

  const handleRestoreDate = async (date) => {
    try {
      await streamApi.restoreSession(date);
      // Refresh schedule
      const data = await streamApi.getSchedule();
      setSchedule(data.schedule || []);
    } catch (error) {
      console.error('Failed to restore session:', error);
      alert('Failed to restore session');
    }
  };

  const openCancelModal = (date) => {
    setSelectedDate(date);
    setShowCancelModal(true);
  };

  // Split schedule into this week and next week
  const thisWeek = schedule.slice(0, 7);
  const nextWeek = schedule.slice(7, 14);

  return (
    <div className="page-content">
      <h1 className="page-title">Stream Manager</h1>
      <p className="page-description">Manage your live trading sessions</p>
      
      <div className="sm-container">
        {/* Card 1: Stream Status */}
        <div className="sm-card">
          <div className="sm-status-row">
            <div className="sm-status-icon">
              <Radio size={32} />
            </div>
            <div className="sm-status-info">
              <h3 className="sm-status-title">Stream Status</h3>
              <span className={`sm-status-badge ${streamData.isLive ? 'live' : 'offline'}`}>
                {streamData.isLive ? 'LIVE' : 'OFFLINE'}
              </span>
            </div>
            <button 
              className={`sm-golive-btn ${streamData.isLive ? 'end' : ''}`}
              onClick={handleGoLive}
              disabled={!streamData.isTodayScheduled}
            >
              {streamData.isLive ? 'End Stream' : 'Go Live'}
            </button>
          </div>
        </div>

        {/* Card 2: Zoom Meeting Link */}
        <div className="sm-card">
          <div className="sm-card-header">
            <div className="sm-header-icon blue">
              <Radio size={20} />
            </div>
            <h3 className="sm-card-title">Zoom Meeting Link</h3>
          </div>

          <div className="sm-form-group">
            <label className="sm-form-label">Zoom Link</label>
            <p className="sm-form-hint">Paste your Zoom meeting link here. Members will click this to join your session.</p>
            <div className="sm-input-wrapper">
              <input 
                type="url"
                className="sm-input"
                value={streamData.zoomLink}
                onChange={(e) => setStreamData({...streamData, zoomLink: e.target.value})}
                placeholder="https://zoom.us/j/1234567890?pwd=..."
              />
              <ArrowUpRight size={16} className="sm-input-icon" />
            </div>
          </div>

          <div className="sm-form-group">
            <label className="sm-form-label">Session Description (Optional)</label>
            <textarea 
              className="sm-textarea small"
              value={streamData.sessionDescription}
              onChange={(e) => setStreamData({...streamData, sessionDescription: e.target.value})}
              placeholder="Today's focus: NAS and XAU analysis..."
              rows="2"
            />
          </div>

          <button 
            className={`sm-save-btn ${zoomSaved ? 'saved' : ''}`}
            onClick={onSaveZoomLink}
            disabled={savingZoom}
          >
            {savingZoom ? (
              <>
                <RefreshCw size={16} className="spin" /> Saving...
              </>
            ) : zoomSaved ? (
              <>
                <Check size={16} /> Saved!
              </>
            ) : (
              'Save Zoom Link'
            )}
          </button>
        </div>

        {/* Card 4: Quick Guide */}
        <div className="sm-card">
          <div className="sm-card-header">
            <div className="sm-header-icon red">
              <span className="sm-guide-icon">1234</span>
            </div>
            <h3 className="sm-card-title">Quick Guide</h3>
          </div>

          <div className="sm-guide-list">
            <div className="sm-guide-item">
              <span className="sm-guide-num">1</span>
              <span className="sm-guide-text">Start your Zoom meeting and copy the invite link</span>
            </div>
            <div className="sm-guide-item">
              <span className="sm-guide-num">2</span>
              <span className="sm-guide-text">Paste the link above and click "Save Zoom Link"</span>
            </div>
            <div className="sm-guide-item">
              <span className="sm-guide-num">3</span>
              <span className="sm-guide-text">Click "Go Live" to enable the join button for members</span>
            </div>
            <div className="sm-guide-item">
              <span className="sm-guide-num">4</span>
              <span className="sm-guide-text">When done, click "End Stream" to hide the button</span>
            </div>
          </div>
        </div>

        {/* Card 5: Weekly Schedule Planner */}
        <div className="sm-card sm-schedule-card">
          <div className="sm-card-header">
            <div className="sm-header-icon purple">
              <Calendar size={20} />
            </div>
            <h3 className="sm-card-title">Weekly Schedule Planner</h3>
          </div>
          <p className="sm-form-hint" style={{ marginBottom: '1rem' }}>
            Plan your cancellations ahead of time. Click on a scheduled day to cancel it.
          </p>

          {loadingSchedule ? (
            <div className="sm-loading">
              <RefreshCw size={20} className="spin" /> Loading schedule...
            </div>
          ) : (
            <>
              {/* This Week */}
              <div className="sm-week-section">
                <h4 className="sm-week-title">This Week</h4>
                <div className="sm-schedule-grid">
                  {thisWeek.map((day) => (
                    <div 
                      key={day.date}
                      className={`sm-day-card ${day.status} ${day.is_today ? 'today' : ''}`}
                      onClick={() => {
                        // Allow editing any day (scheduled or cancelled), skip off/past days
                        if (day.status === 'scheduled' || day.status === 'live') {
                          openCancelModal(day.date);
                        } else if (day.status === 'cancelled') {
                          handleRestoreDate(day.date);
                        }
                      }}
                    >
                      <span className="sm-day-name">{day.day_short}</span>
                      <span className="sm-day-num">{day.day_num}</span>
                      {day.status === 'scheduled' && <span className="sm-day-status">✓</span>}
                      {day.status === 'cancelled' && (
                        <>
                          <span className="sm-day-status cancelled">✕</span>
                          {day.cancellation_reason && (
                            <span className="sm-cancel-reason">{day.cancellation_reason}</span>
                          )}
                        </>
                      )}
                      {day.status === 'off' && <span className="sm-day-status off">—</span>}
                      {day.status === 'live' && <span className="sm-day-status live">LIVE</span>}
                      {day.status === 'past' && <span className="sm-day-status past">—</span>}
                      {day.is_today && <span className="sm-today-badge">TODAY</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Next Week */}
              <div className="sm-week-section">
                <h4 className="sm-week-title">Next Week</h4>
                <div className="sm-schedule-grid">
                  {nextWeek.map((day) => (
                    <div 
                      key={day.date}
                      className={`sm-day-card ${day.status}`}
                      onClick={() => {
                        if (day.status === 'scheduled') {
                          openCancelModal(day.date);
                        } else if (day.status === 'cancelled') {
                          handleRestoreDate(day.date);
                        }
                      }}
                    >
                      <span className="sm-day-name">{day.day_short}</span>
                      <span className="sm-day-num">{day.day_num}</span>
                      {day.status === 'scheduled' && <span className="sm-day-status">✓</span>}
                      {day.status === 'cancelled' && (
                        <>
                          <span className="sm-day-status cancelled">✕</span>
                          {day.cancellation_reason && (
                            <span className="sm-cancel-reason">{day.cancellation_reason}</span>
                          )}
                        </>
                      )}
                      {day.status === 'off' && <span className="sm-day-status off">—</span>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="sm-schedule-legend">
                <span className="legend-item"><span className="dot scheduled"></span> Scheduled</span>
                <span className="legend-item"><span className="dot cancelled"></span> Cancelled</span>
                <span className="legend-item"><span className="dot off"></span> Off Day</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Cancel Session Modal */}
      {showCancelModal && (
        <div className="sm-modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="sm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sm-modal-header">
              <AlertCircle size={24} className="sm-modal-icon" />
              <h3>Cancel Session</h3>
              <button className="sm-modal-close" onClick={() => setShowCancelModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="sm-modal-body">
              <p>Cancel the session for <strong>{selectedDate}</strong>?</p>
              <div className="sm-form-group">
                <label className="sm-form-label">Reason (shown to members)</label>
                <input
                  type="text"
                  className="sm-input"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g., FOMC news day, Holiday, Out of town"
                />
              </div>
            </div>
            <div className="sm-modal-actions">
              <button className="sm-btn-secondary" onClick={() => setShowCancelModal(false)}>
                Never mind
              </button>
              <button className="sm-btn-danger" onClick={handlePlanCancellation}>
                Cancel Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StreamManagerPage;